import React from "react";
import { Controller } from "react-hook-form";
import { formatCurrency as utilFormatCurrency } from "../../../../utils/format";

/**
 * ControlledField
 * A small wrapper around react-hook-form's Controller that standardizes
 * numeric input behavior: cleaning on change and formatting on blur.
 *
 * Props:
 * - name, control: react-hook-form Controller props
 * - InputComponent: component to render (FloatingInput or FloatingNumber)
 * - label, placeholder, type
 * - cleanNumber: function to clean live input (e.g., remove non-digits)
 * - handleCurrencyBlur: optional hook (field, raw) => formatted string
 * - onFormatted: optional callback(formatted) -- e.g., parent can sync state
 * - handleCalAmount: optional callback to run after blur
 */
export default function ControlledField({
  name,
  control,
  InputComponent,
  label,
  placeholder,
  type = "text",
  cleanNumber,
  handleCurrencyBlur,
  onFormatted,
  handleCalAmount,
  defaultValue = "",
  inputProps = {},
}) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <InputComponent
          {...field}
          placeholder={placeholder}
          label={label}
          type={type}
          value={defaultValue}
          {...inputProps}
          onChange={(e) => {
            const raw = e?.target?.value ?? "";
            const cleaned = cleanNumber ? cleanNumber(raw) : raw;
            field.onChange(cleaned);
          }}
          onBlur={(e) => {
            // notify RHF
            field.onBlur?.(e);

            const rawValue = e?.target?.value ?? field.value ?? "";
            const cleaned = cleanNumber
              ? cleanNumber(String(rawValue))
              : String(rawValue).replace(/[^0-9.]/g, "");

            // If empty, skip formatting
            if (cleaned === "") return;

            let formatted = null;

            // Try currency blur function safely
            try {
              const maybe = handleCurrencyBlur
                ? handleCurrencyBlur(name, cleaned)
                : null;
              if (typeof maybe === "string" && maybe !== "") {
                formatted = maybe;
              }
            } catch {
              // ignore
            }

            // fallback
            if (!formatted) {
              formatted = utilFormatCurrency(cleaned);
            }

            field.onChange(formatted);

            // Only notify the parent if this genuinely differs from what's
            // actually stored — field.value already mirrors every keystroke,
            // so it can't tell a real edit apart from an untouched value.
            if (formatted !== String(defaultValue ?? "")) {
              onFormatted?.(name, formatted);
            }
          }}
        />
      )}
    />
  );
}
